/**
 * ConnectivityChip — header indicator for online / offline / saving / failed.
 *
 * Replaces the ad-hoc inline Chip both screens were rendering. Adds visual
 * urgency: when offline or save failed the chip pulses softly to draw the
 * scout's eye, since losing data on a tablet is the worst outcome.
 *
 * States:
 *   - online   → green check, no animation
 *   - offline  → red wifi-off, pulse
 *   - saving   → blue spinner, no pulse
 *   - failed   → red error, fast pulse
 */

import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';

export default function ConnectivityChip({
  isOnline = true,
  isSaving = false,
  saveFailed = false,
  t,
}) {
  const theme = useStatsTheme();
  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  // Resolve current state (priority: failed > saving > offline > online)
  let icon;
  let label;
  let color;
  let pulse = false;
  let pulseSpeed = '1.6s';

  if (saveFailed) {
    icon = <ErrorOutlineIcon sx={{ fontSize: 14 }} />;
    label = tt('saveError', 'save error');
    color = theme.actionColors.missed;
    pulse = true;
    pulseSpeed = '0.9s';
  } else if (isSaving) {
    icon = <CircularProgress size={12} sx={{ color: theme.actionColors.info }} />;
    label = tt('saving', 'saving…');
    color = theme.actionColors.info;
  } else if (!isOnline) {
    icon = <WifiOffIcon sx={{ fontSize: 14 }} />;
    label = tt('offline', 'offline');
    color = theme.actionColors.missed;
    pulse = true;
  } else {
    icon = <CloudDoneIcon sx={{ fontSize: 14 }} />;
    label = tt('online', 'online');
    color = theme.actionColors.made;
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={label}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        height: 26,
        px: 1,
        borderRadius: theme.radius.pill,
        backgroundColor: withAlpha(color, 0.16),
        border: `1px solid ${withAlpha(color, 0.4)}`,
        color,
        fontWeight: 700,
        fontSize: 10,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        position: 'relative',
        ...(pulse && {
          animation: `connectivityPulse ${pulseSpeed} ease-in-out infinite`,
          '@keyframes connectivityPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0 ${withAlpha(color, 0.55)}` },
            '50%':      { boxShadow: `0 0 0 6px ${withAlpha(color, 0)}` },
          },
        }),
      }}
    >
      {icon}
      <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
        {label}
      </Typography>
    </Box>
  );
}
